import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useUserRoles } from '@anytag/auth';
import { YoutubeCmsEstimateAsset } from '@anytag/graphql';
import { TableCell, TextCutter } from '@anytag/shared-atoms';
import { formatNumber, formatPercent } from '@anytag/shared-utils';

export const useYoutubeCMSChannelsAssetsTable = () => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();

  const columns: ColumnDef<YoutubeCmsEstimateAsset>[] = [
    {
      accessorKey: 'assetName',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Asset name'),
      cell: ({ row }) => (
        <div className="flex min-w-50 items-center">
          <div className="leading-[100%]">
            <TextCutter className="text-base" text={row.original.assetName || '-'} lines={2} />
            <p className="mt-1 text-xxs text-tertiary">
              {t('id')}: {row.original.assetId}
            </p>
          </div>
          {row.original.isOwner ? (
            <div className="ml-4 box-border w-max rounded-[35px] bg-black-20 px-2 py-0.75">{t('Owner')}</div>
          ) : null}
        </div>
      )
    },
    {
      accessorKey: 'rpm',
      header: t<string>('HeaderColumn.Estimated RPM'),
      meta: { align: 'right', hidden: !isAdminStaff },
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.rpm)} unit={t(row.original.currency)} />
    },
    {
      accessorKey: 'taRpm',
      header: t<string>('HeaderColumn.Estimated talent agency RPM'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.taRpm)} unit={t(row.original.currency)} />
    },
    {
      accessorKey: 'partnerRevenue',
      header: t<string>('HeaderColumn.Estimated partner revenue'),
      meta: { align: 'right', hidden: !isAdminStaff },
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.partnerRevenue)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'taRevenue',
      header: t<string>('HeaderColumn.Estimated talent agency revenue'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.taRevenue)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'shareCreator',
      header: t<string>('HeaderColumn.Estimated share to creator'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.shareCreator)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'profit',
      header: t<string>('HeaderColumn.Estimated GP'),
      meta: { align: 'right', hidden: !isAdminStaff },
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.profit || null)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'taProfit',
      header: t<string>('HeaderColumn.Estimated talent agency GP'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.taProfit || null)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'taShareRate',
      header: t<string>('HeaderColumn.Talent agency margin rate'),
      meta: { align: 'right', hidden: !isAdminStaff },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.taShareRate, false)} unit="%" />
    }
  ];

  return { columns };
};
